import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, Phone, Facebook } from "lucide-react";

const navigation = [
  { name: "Home", href: "#home" },
  { name: "About", href: "#about" },
  { name: "Services", href: "#services" },
  { name: "Products", href: "#products" },
  { name: "Machinery", href: "#machinery" },
  { name: "Contact", href: "#contact" },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = navigation.map(n => n.href.replace('#', ''));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element && window.scrollY >= element.offsetTop - 200) {
          setActiveSection(section);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-500 bg-white ${
        scrolled ? 'shadow-lg' : 'shadow-sm'
      }`}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        WebkitTransform: 'translate3d(0,0,0)', 
        transform: 'translate3d(0,0,0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        willChange: 'transform'
      }}
      data-testid="header"
    >
      {/* Desktop: Logo Row - Phone | Logo | Facebook */}
      <div className={`hidden lg:block transition-all duration-500 ${scrolled ? 'py-3' : 'py-6'} border-b border-gray-200`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between relative">
            {/* Phone on left */}
            <a 
              href="tel:01482221098" 
              className="flex items-center gap-2 text-sm font-medium transition-all duration-300 hover:scale-105 text-[#0F2557]"
              data-testid="phone-link-header"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 bg-[#0F2557]/10">
                <Phone className="h-4 w-4" />
              </div>
              <span>01482 221098</span>
            </a>
            
            {/* Logo centered */}
            <a 
              href="#home"
              onClick={(e) => scrollToSection(e, '#home')}
              className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2 transition-transform duration-300 hover:scale-105"
              data-testid="logo-link"
            >
              <img 
                src="/images/logo.webp" 
                alt="Fox Precision Engineering Ltd"
                width="600"
                height="155"
                fetchpriority="high"
                decoding="async"
                className={`transition-all duration-500 ${scrolled ? 'h-12' : 'h-14'} w-auto`}
              />
            </a>
            
            {/* Facebook on right */}
            <a 
              href="https://www.facebook.com/FoxPrecisionEngineeringLtd/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium transition-all duration-300 hover:scale-105 text-[#0F2557]"
              data-testid="facebook-link-header"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 bg-[#0F2557]/10">
                <Facebook className="h-4 w-4" />
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Desktop: Navigation Row */}
      <nav className={`hidden lg:block transition-all duration-500 ${scrolled ? 'border-b border-gray-100' : ''}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-center gap-12 transition-all duration-500 ${scrolled ? 'h-12' : 'h-14'}`}>
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className={`nav-premium text-sm font-medium transition-all duration-300 cursor-pointer relative ${
                  activeSection === item.href.replace('#', '')
                    ? 'text-[#0F2557]'
                    : 'text-gray-500 hover:text-[#0F2557]'
                } ${activeSection === item.href.replace('#', '') ? 'active' : ''}`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <div className={`lg:hidden flex items-center justify-between px-4 transition-all duration-500 h-16 bg-white ${scrolled ? 'shadow-lg' : 'shadow-sm'}`}>
        <a href="#home" onClick={(e) => scrollToSection(e, '#home')}>
          <img 
            src="/images/logo.webp" 
            alt="Fox Precision Engineering Ltd"
            width="600"
            height="155"
            fetchpriority="high"
            decoding="async"
            className="h-10 w-auto"
          />
        </a>
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-[#0F2557]"
              data-testid="mobile-menu-trigger"
            >
              <Menu className="h-6 w-6" />
              <span className="sr-only">Open menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-sm bg-white border-l-0">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-12">
                <img 
                  src="/images/logo.webp" 
                  alt="Fox Precision Engineering Ltd"
                  width="600"
                  height="155"
                  decoding="async"
                  className="h-12 w-auto"
                />
              </div>
              
              <nav className="flex flex-col gap-2">
                {navigation.map((item, index) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    className="text-2xl font-bold py-4 text-[#1A1D20] hover:text-[#0F2557] transition-all duration-300 border-b border-gray-100"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                  >
                    {item.name}
                  </a>
                ))}
              </nav>

              <div className="mt-auto pt-8 space-y-4">
                <a 
                  href="tel:01482221098" 
                  className="flex items-center justify-center gap-3 py-4 text-[#0F2557] font-bold border-2 border-[#0F2557] transition-all duration-300 hover:bg-[#0F2557] hover:text-white"
                  data-testid="mobile-phone-link"
                >
                  <Phone className="h-5 w-5" />
                  01482 221098
                </a>
                <a 
                  href="#contact"
                  onClick={(e) => scrollToSection(e, '#contact')}
                  className="block"
                >
                  <Button 
                    className="w-full bg-[#0F2557] hover:bg-[#0F2557]/90 text-white rounded-none py-6 text-lg font-bold"
                    data-testid="mobile-contact-us"
                  >
                    Contact Us
                  </Button>
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
